import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ordersAPI } from '../../services/storeClubAPI';
import ClubLayout from '../../components/StoreClub/ClubLayout';

interface OrderItem {
  id: number;
  product_name: string;
  quantity: number;
  unit_price: string;
  line_total: string;
}

interface Order {
  id: number;
  order_number: string;
  status: string;
  status_display: string;
  pickup_type: string;
  pickup_type_display: string;
  subtotal: string;
  locker_fee: string;
  total: string;
  locker_code: string | null;
  locker_number: number | null;
  items: OrderItem[];
  created_at: string;
  ready_at: string | null;
  picked_up_at: string | null;
}

const ClubOrders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'active' | 'completed'>('all');

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const data = await ordersAPI.getAll();
      setOrders(data);
    } catch (error) {
      console.error('Failed to load orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = orders.filter((order) => {
    if (filter === 'active') {
      return ['pending', 'processing', 'ready'].includes(order.status);
    }
    if (filter === 'completed') {
      return ['picked_up', 'cancelled'].includes(order.status);
    }
    return true;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return '#ffc107';
      case 'processing': return '#2196f3';
      case 'ready': return '#4caf50';
      case 'picked_up': return '#9e9e9e';
      case 'cancelled': return '#f44336';
      default: return '#9e9e9e';
    }
  };

  if (loading) {
    return (
      <ClubLayout>
        <div style={styles.loading}>Loading orders...</div>
      </ClubLayout>
    );
  }

  return (
    <ClubLayout>
      <div style={styles.content}>
        <h2 style={styles.heading}>My Orders</h2>

        <div style={styles.filters}>
          {(['all', 'active', 'completed'] as const).map((f) => (
            <button
              key={f}
              style={{
                ...styles.filterButton,
                backgroundColor: filter === f ? '#508590' : '#fff',
                color: filter === f ? '#fff' : '#333',
              }}
              onClick={() => setFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {filteredOrders.length === 0 ? (
          <div style={styles.empty}>
            <div style={styles.emptyIcon}>📦</div>
            <p style={styles.emptyText}>No orders yet</p>
            <Link to="/club/shop" style={styles.shopLink}>
              Start Shopping
            </Link>
          </div>
        ) : (
          <div style={styles.orderList}>
            {filteredOrders.map((order) => (
              <Link
                key={order.id}
                to={`/club/orders/${order.id}`}
                style={styles.orderCard}
              >
                <div style={styles.orderHeader}>
                  <div style={styles.orderMain}>
                    <span style={styles.orderNumber}>#{order.order_number}</span>
                    <span style={styles.orderDate}>
                      {new Date(order.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <span
                    style={{
                      ...styles.orderStatus,
                      backgroundColor: getStatusColor(order.status),
                    }}
                  >
                    {order.status_display}
                  </span>
                </div>

                <div style={styles.orderItems}>
                  {order.items.slice(0, 2).map((item) => (
                    <span key={item.id} style={styles.orderItem}>
                      {item.quantity}x {item.product_name}
                    </span>
                  ))}
                  {order.items.length > 2 && (
                    <span style={styles.moreItems}>
                      +{order.items.length - 2} more
                    </span>
                  )}
                </div>

                <div style={styles.orderFooter}>
                  <span style={styles.pickupType}>
                    {order.pickup_type === 'locker' ? '🔐 Locker' : '🏪 Store'} Pickup
                  </span>
                  <span style={styles.orderTotal}>${order.total}</span>
                </div>

                {order.status === 'ready' && order.locker_code && (
                  <div style={styles.lockerInfo}>
                    <span style={styles.lockerLabel}>Your Locker Code:</span>
                    <span style={styles.lockerCode}>{order.locker_code}</span>
                    <span style={styles.lockerNumber}>Locker #{order.locker_number}</span>
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    </ClubLayout>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  content: {
    padding: '1rem',
    paddingBottom: '5rem',
    maxWidth: '600px',
    margin: '0 auto',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '50vh',
    color: '#666',
    fontSize: '1.125rem',
  },
  heading: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#333',
    margin: '0 0 1rem 0',
  },
  filters: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1.5rem',
  },
  filterButton: {
    padding: '0.5rem 1rem',
    border: '2px solid #e0e0e0',
    borderRadius: '20px',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontFamily: "'Open Sans', sans-serif",
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem',
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: '3rem',
    marginBottom: '1rem',
  },
  emptyText: {
    color: '#666',
    margin: '0 0 1rem 0',
  },
  shopLink: {
    backgroundColor: '#508590',
    color: '#fff',
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    textDecoration: 'none',
    fontWeight: 600,
    fontSize: '0.9375rem',
  },
  orderList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  orderCard: {
    display: 'block',
    backgroundColor: '#fff',
    padding: '1rem',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    textDecoration: 'none',
  },
  orderHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '0.75rem',
  },
  orderMain: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.125rem',
  },
  orderNumber: {
    fontWeight: 'bold',
    color: '#333',
    fontSize: '1rem',
  },
  orderDate: {
    fontSize: '0.8125rem',
    color: '#666',
  },
  orderStatus: {
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    color: '#fff',
    fontSize: '0.6875rem',
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  orderItems: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    marginBottom: '0.75rem',
    paddingBottom: '0.75rem',
    borderBottom: '1px solid #f0f0f0',
  },
  orderItem: {
    fontSize: '0.875rem',
    color: '#333',
  },
  moreItems: {
    fontSize: '0.8125rem',
    color: '#666',
    fontStyle: 'italic',
  },
  orderFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  pickupType: {
    fontSize: '0.8125rem',
    color: '#666',
  },
  orderTotal: {
    fontWeight: 'bold',
    color: '#E73C3E',
    fontSize: '1.125rem',
  },
  lockerInfo: {
    marginTop: '0.75rem',
    padding: '0.75rem',
    backgroundColor: '#e8f5e9',
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.25rem',
  },
  lockerLabel: {
    fontSize: '0.75rem',
    color: '#2e7d32',
    fontWeight: 500,
  },
  lockerCode: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#2e7d32',
    letterSpacing: '2px',
  },
  lockerNumber: {
    fontSize: '0.75rem',
    color: '#666',
  },
};

export default ClubOrders;
